import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MAIN_NAV } from "@/config/nav";
import { BRAND } from "@/config/brand";
import { useCta } from "@/hooks/useCta";
import { useAuth } from "@/hooks/useAuth";

interface HeaderProps {
  variant?: 'transparent' | 'solid';
}

export function Header({ variant = 'solid' }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { handleCtaClick } = useCta();
  const { isAuthenticated } = useAuth();
  
  const isActive = (href: string) => location.pathname === href;
  const isTransparent = variant === 'transparent';
  
  return (
    <header data-app-header="true" className={`sticky top-0 z-50 w-full border-b border-border/20 ${isTransparent ? 'bg-background/80' : 'bg-background'} backdrop-blur-xl mt-2`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <img 
            src={isTransparent ? BRAND.logoLight : BRAND.logoDark} 
            alt={BRAND.alt} 
            className="h-24 w-auto px-2" 
            width="140"
            height="96"
            loading="eager"
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
          {MAIN_NAV.map((item) => (
            <Link 
              key={item.href}
              to={item.href} 
              className={`text-sm font-medium transition-colors duration-200 relative ${
                isActive(item.href) 
                  ? 'text-primary border-b-2 border-primary pb-1' 
                  : 'text-muted-foreground hover:text-foreground'
              } after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-1/2 after:bg-primary after:transition-all after:duration-300 after:ease-out hover:after:w-full hover:after:left-0`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        
        {/* Desktop CTA + Mobile Menu Button */}
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-2">
            <Button 
              variant="cta-secondary" 
              size="default" 
              className="text-sm"
              asChild
            >
              <Link to="/espace-client">
                Espace client
              </Link>
            </Button>
            <Button 
              variant="cta-primary" 
              size="default" 
              className="text-sm"
              asChild
            >
              <Link to="/audit-gratuit">
                Audit gratuit
              </Link>
            </Button>
          </div>
          <Button 
            variant="cta-primary" 
            size="default" 
            className="lg:hidden text-sm"
            asChild
          >
            <Link to="/audit-gratuit">
              Audit
            </Link>
          </Button>
          
          {/* Mobile Menu Button */}
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 border-t border-border/20 bg-background/95 backdrop-blur-xl">
            <nav className="container mx-auto px-6 py-4 space-y-3">
              {MAIN_NAV.map((item) => (
                <Link 
                  key={item.href}
                  to={item.href}
                  className={`block py-2 text-sm transition-colors ${
                    isActive(item.href) 
                      ? 'text-primary font-semibold' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-3 space-y-3 sm:hidden">
                <Button 
                  variant="cta-secondary" 
                  size="lg" 
                  className="w-full"
                  asChild
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link to="/espace-client">
                    Espace client
                  </Link>
                </Button>
                <Button 
                  variant="cta-primary" 
                  size="lg" 
                  className="w-full"
                  asChild
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link to="/audit-gratuit">
                    Audit Amazon gratuit
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}